import { Button } from "@ecco/ui/button";
import { Input } from "@ecco/ui/input";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import { styles } from "../../PromoCodeRow.styles";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

type Props = {
    discountCharsLimit: number;
    errorMessage: string;
    isApplying: boolean;
    onApply: (id: string, clearValue: () => void) => void;
    headers: {
        apply: string;
        applyAria: string;
    };
    setDraftPromoCode: (value: string) => void;
    isLocked: boolean;
    setAddedPromocodesErrors: (errors: { id?: string; message?: string; code?: string }[]) => void;
};

export const PromoInput = ({
    discountCharsLimit,
    setDraftPromoCode,
    errorMessage,
    isApplying,
    onApply,
    isLocked,
    headers,
    setAddedPromocodesErrors,
}: Props) => {
    const { apply, applyAria } = headers;
    const [value, setValue] = useState("");

    const onChange = (event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value);

    const onEnterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === "Enter") {
            onApply(value, clearValue);
        }
    };

    const clearValue = () => {
        setValue("");
    };

    const onClickApply = () => {
        setAddedPromocodesErrors([]);
        onApply(value, clearValue);
    };

    useEffect(() => {
        const timeOutId = setTimeout(() => setDraftPromoCode(value), 500);
        return () => clearTimeout(timeOutId);
    }, [value]);

    return (
        <Box pb={1} px={0}>
            <Flex flexDirection={"row"} gap={2} justify="space-between">
                <Flex sx={styles.input}>
                    <Input
                        fontWeight="bold"
                        value={value}
                        onChange={onChange}
                        maxLength={discountCharsLimit}
                        colorScheme={"red"}
                        // eslint-disable-next-line no-extra-boolean-cast
                        borderColor={Boolean(errorMessage) ? "red.200" : "black"}
                        // eslint-disable-next-line no-extra-boolean-cast
                        color={Boolean(errorMessage) ? "red.200" : "inherit"}
                        onKeyDown={onEnterKeyDown}
                        _focusVisible={{ outline: "none" }}
                        _focus={{ outline: "none" }}
                        _selected={{ outline: "none" }}
                        width="100%"
                    />
                </Flex>
                <Button
                    isDisabled={isLocked}
                    onClick={onClickApply}
                    sx={styles.applyButton}
                    isLoading={isApplying}
                    aria-label={applyAria}
                    data-dd-action-name={RUM_ACTION_NAME.APPLY_PROMO_CODE}
                >
                    <span>{apply}</span>
                </Button>
            </Flex>
            {errorMessage && (
                <Box
                    mt="2"
                    color={"red.200"}
                    fontWeight={"semibold"}
                    fontSize={["mobileBodyTextSmall", "desktopBodyTextTiny"]}
                >
                    {errorMessage}
                </Box>
            )}
        </Box>
    );
};
